import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Button,
    Paper,
    Stack,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    Backdrop
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowBack as ArrowBackIcon,
    Add as AddIcon,
    CheckCircle as CheckCircleIcon,
    Send as SendIcon,
    CalendarToday as CalendarIcon,
    Close as CloseIcon,
    RadioButtonUnchecked as PendingIcon,
    Create as SignIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import tbtSubmissionService from '../../services/tbtSubmissionService';
import SelectEmployeesAndTempsModal from '../jha/SelectEmployeesAndTempsModal';
import TBTAcknowledgmentModal from './TBTAcknowledgmentModal';
import TBTInstructionViewModal from './TBTInstructionViewModal';
import SelectFromWeeklyRosterModal from '../jha/SelectFromWeeklyRosterModal';
import { initializeDB } from '../../config/db';
import { TOOLBOXTALK_STORE, TBT_SUBMISSION_STORE } from '../../config/constants';

const TBTForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [submission, setSubmission] = useState(null);
    const [tbtTopic, setTbtTopic] = useState(null);
    const [loading, setLoading] = useState(true);
    const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
    const [weeklyRosterModalOpen, setWeeklyRosterModalOpen] = useState(false);
    const [acknowledgmentModalOpen, setAcknowledgmentModalOpen] = useState(false);
    const [instructorViewModalOpen, setInstructorViewModalOpen] = useState({ open: false, language: 'english' });
    const [selectedEmployeeIndex, setSelectedEmployeeIndex] = useState(null);
    const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        loadSubmission();
    }, [id]);

    const loadSubmission = async () => {
        setLoading(true);
        try {
            const data = await tbtSubmissionService.getById(id);
            if (!data) {
                navigate('/tbt');
                return;
            }
            setSubmission(data);

            // Load TBT topic from toolboxtalks store
            const db = await initializeDB();
            const topic = await db.get(TOOLBOXTALK_STORE, data.topicId);
            setTbtTopic(topic);
        } catch (error) {
            console.error('Error loading TBT submission:', error);
            navigate('/tbt');
        } finally {
            setLoading(false);
        }
    };

    const handleAddEmployees = async (selectedPeople) => {
        try {
            let updated = submission;
            // Filter out employees that are already in the submission
            const newPeople = selectedPeople.filter(person => {
                const isDuplicate = submission.employees.some(emp => {
                    // Normalize names for comparison
                    const empName = (emp.name || '').trim().toLowerCase();
                    const personName = (person.FullName || '').trim().toLowerCase();
                    const empType = emp._type || emp.type;
                    const personType = person._type;

                    return empName === personName && empType === personType;
                });
                return !isDuplicate;
            });

            // Add each new person
            for (const person of newPeople) {
                const employee = {
                    name: person.FullName,
                    employeeId: person.EE || person.ee || person.id,
                    firstName: person.FullName.split(' ')[0],
                    lastName: person.FullName.split(' ').slice(1).join(' '),
                    division: person.Division || person.Agency,
                    type: person._type,
                    _type: person._type
                };
                updated = await tbtSubmissionService.addEmployee(id, employee);
            }
            setSubmission(updated);
            setEmployeeModalOpen(false);
        } catch (error) {
            console.error('Error adding employees:', error);
        }
    };

    const handleRemoveEmployee = async (index) => {
        try {
            const db = await initializeDB();
            const updatedEmployees = [...submission.employees];
            updatedEmployees.splice(index, 1);

            const tx = db.transaction(TBT_SUBMISSION_STORE, 'readwrite');
            const store = tx.objectStore(TBT_SUBMISSION_STORE);

            const updated = {
                ...submission,
                employees: updatedEmployees,
                updatedAt: new Date().toISOString()
            };

            await store.put(updated);
            await tx.done;

            setSubmission(updated);
        } catch (error) {
            console.error('Error removing employee:', error);
        }
    };

    const getEmployeeStatus = (employee) => {
        if (!employee.signature) return 'pending';
        if (employee.signature && employee.signedAt) return 'signed';
        return 'pending';
    };

    const handleSelectFromRoster = async (selectedPeople) => {
        try {
            let updated = submission;
            // Filter out employees that are already in the submission
            const newPeople = selectedPeople.filter(person => {
                const isDuplicate = submission.employees.some(emp => {
                    // Normalize names for comparison
                    const empName = (emp.name || '').trim().toLowerCase();
                    const personName = (person.FullName || '').trim().toLowerCase();
                    const empType = emp._type || emp.type;
                    const personType = person._type;

                    return empName === personName && empType === personType;
                });
                return !isDuplicate;
            });

            // Add each new person from roster
            for (const person of newPeople) {
                const employee = {
                    name: person.FullName,
                    employeeId: person.EE || person.ee || person.id,
                    firstName: person.FullName.split(' ')[0],
                    lastName: person.FullName.split(' ').slice(1).join(' '),
                    division: person.Division || person.Agency,
                    type: person._type,
                    _type: person._type
                };
                updated = await tbtSubmissionService.addEmployee(id, employee);
            }
            setSubmission(updated);
            setWeeklyRosterModalOpen(false);
        } catch (error) {
            console.error('Error adding employees from roster:', error);
        }
    };

    const handleSignClick = (index) => {
        setSelectedEmployeeIndex(index);
        setAcknowledgmentModalOpen(true);
    };

    const handleSignSubmit = async (signature) => {
        if (selectedEmployeeIndex !== null) {
            try {
                const updated = await tbtSubmissionService.signEmployee(
                    id,
                    selectedEmployeeIndex,
                    signature
                );
                setSubmission(updated);

                // Only close modals if signature was added (not removed)
                if (signature) {
                    setAcknowledgmentModalOpen(false);
                    setSelectedEmployeeIndex(null);
                }
            } catch (error) {
                console.error('Error signing employee:', error);
            }
        }
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        setSubmitDialogOpen(false);

        try {
            const result = await tbtSubmissionService.submit(id);

            await Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: result.message || 'Tool Box Talk submitted successfully',
                confirmButtonColor: '#4caf50'
            });

            navigate('/tbt');
        } catch (error) {
            console.error('Error submitting TBT:', error);

            await Swal.fire({
                icon: 'error',
                title: 'Submission Failed',
                text: error.message || 'Failed to submit Tool Box Talk. Please try again.',
                confirmButtonColor: '#f44336'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const getSignedCount = () => {
        if (!submission || !submission.employees) return 0;
        return submission.employees.filter(emp => emp.signature).length;
    };

    const getTotalCount = () => {
        if (!submission || !submission.employees) return 0;
        return submission.employees.length;
    };

    const canSubmit = () => {
        return submission && submission.employees && submission.employees.length > 0;
    };

    if (loading) {
        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh'
            }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!submission) {
        return null;
    }

    const signedCount = getSignedCount();
    const totalCount = getTotalCount();

    return (
        <Box sx={{
            position: 'fixed',
            top: '60px',
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: 'background.default'
        }}>
            {/* Header */}
            <Box
                sx={{
                    p: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                    flex: '0 0 auto'
                }}
            >
                {/* Title Row with Action Buttons */}
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <IconButton onClick={() => navigate('/tbt')} color="primary" size="large">
                        <ArrowBackIcon />
                    </IconButton>
                    <Box flex={1}>
                        <Typography variant="h5" fontWeight={600}>{submission.topicTitle}</Typography>
                        <Typography variant="subtitle2" color="text.secondary">
                            Signatures: {signedCount} / {totalCount}
                        </Typography>
                    </Box>
                    <Box display="flex" gap={1.5}>
                        <Button
                            variant="outlined"
                            startIcon={<CalendarIcon />}
                            onClick={() => setWeeklyRosterModalOpen(true)}
                            sx={{
                                borderColor: '#ff9800',
                                color: '#ff9800',
                                '&:hover': {
                                    borderColor: '#f57c00',
                                    backgroundColor: 'rgba(255, 152, 0, 0.04)'
                                }
                            }}
                        >
                            From Roster
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setEmployeeModalOpen(true)}
                            sx={{
                                backgroundColor: '#2196f3',
                                '&:hover': { backgroundColor: '#1976d2' }
                            }}
                        >
                            Add Members
                        </Button>
                    </Box>
                </Box>

                {/* View Instruction Buttons Row */}
                {tbtTopic && (
                    <Box display="flex" gap={1.5}>
                        <Button
                            variant="outlined"
                            onClick={() => setInstructorViewModalOpen({ open: true, language: 'english' })}
                            sx={{ minWidth: 140 }}
                        >
                            View English
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => setInstructorViewModalOpen({ open: true, language: 'spanish' })}
                            sx={{ minWidth: 140 }}
                        >
                            View Spanish
                        </Button>
                    </Box>
                )}
            </Box>

            {/* Content */}
            <Box sx={{ flex: '1 1 auto', overflow: 'auto', p: 2 }}>
                {totalCount === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body1" color="text.secondary">
                            No employees added yet. Click "Add Members" to start.
                        </Typography>
                    </Paper>
                ) : (
                    <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                        <Stack spacing={1}>
                            {submission.employees.map((employee, index) => {
                                const status = getEmployeeStatus(employee);
                                const statusConfig = {
                                    pending: { color: '#f44336', icon: PendingIcon, label: 'Pending' },
                                    signed: { color: '#4caf50', icon: CheckCircleIcon, label: 'Signed' }
                                };
                                const StatusIcon = statusConfig[status].icon;
                                const divisionOrAgency = employee.division;

                                return (
                                    <Box
                                        key={`member-${employee.name}-${employee._type}-${index}`}
                                        onClick={() => handleSignClick(index)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            p: 0.75,
                                            backgroundColor: 'white',
                                            border: status === 'signed' ? '1px solid #4caf50' : '1px solid #e0e0e0',
                                            borderRadius: 1,
                                            cursor: 'pointer',
                                            '&:hover': { backgroundColor: '#f5f5f5' }
                                        }}
                                    >
                                        <StatusIcon sx={{ fontSize: 16, color: statusConfig[status].color, flexShrink: 0 }} />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {employee.name || `${employee.firstName} ${employee.lastName}`}
                                                {divisionOrAgency && (
                                                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                                        ({divisionOrAgency})
                                                    </Typography>
                                                )}
                                            </Typography>
                                            {employee.signedAt && (
                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                    {new Date(employee.signedAt).toLocaleString()}
                                                </Typography>
                                            )}
                                        </Box>
                                        {status === 'signed' && employee.signature && (
                                            <Box
                                                sx={{
                                                    width: 50,
                                                    height: 24,
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: 0.5,
                                                    overflow: 'hidden',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <img src={employee.signature} alt="signature" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                            </Box>
                                        )}
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveEmployee(index);
                                            }}
                                            sx={{ color: '#999', p: 0.25 }}
                                        >
                                            <CloseIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Box>
                )}

                {/* Submit Button at Bottom */}
                {totalCount > 0 && (
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<SendIcon />}
                            onClick={() => setSubmitDialogOpen(true)}
                            disabled={!canSubmit()}
                            sx={{ minWidth: 200 }}
                        >
                            Submit
                        </Button>
                    </Box>
                )}
            </Box>

            {/* Employee Selection Modal */}
            <SelectEmployeesAndTempsModal
                open={employeeModalOpen}
                onClose={() => setEmployeeModalOpen(false)}
                onSelect={handleAddEmployees}
                alreadySelected={submission.employees.map(emp => ({
                    FullName: emp.name,
                    EE: emp.employeeId,
                    ee: emp.employeeId,
                    id: emp.employeeId,
                    Division: emp.division,
                    Agency: emp.division,
                    _type: emp._type || emp.type
                }))}
                singleSelect={false}
            />

            {/* Weekly Roster Selection Modal */}
            <SelectFromWeeklyRosterModal
                open={weeklyRosterModalOpen}
                onClose={() => setWeeklyRosterModalOpen(false)}
                onSelect={handleSelectFromRoster}
                alreadySelected={submission.employees.map(emp => ({
                    FullName: emp.name,
                    EE: emp.employeeId,
                    ee: emp.employeeId,
                    id: emp.employeeId,
                    Division: emp.division,
                    Agency: emp.division,
                    _type: emp._type || emp.type
                }))}
            />

            {/* TBT Acknowledgment Modal for Employees */}
            {selectedEmployeeIndex !== null && submission.employees[selectedEmployeeIndex] && tbtTopic && (
                <TBTAcknowledgmentModal
                    open={acknowledgmentModalOpen}
                    onClose={() => {
                        setAcknowledgmentModalOpen(false);
                        setSelectedEmployeeIndex(null);
                    }}
                    employee={submission.employees[selectedEmployeeIndex]}
                    tbtTopic={tbtTopic}
                    existingSignature={submission.employees[selectedEmployeeIndex].signature}
                    onSign={handleSignSubmit}
                />
            )}

            {/* TBT Instruction View for Instructor */}
            {tbtTopic && (
                <TBTInstructionViewModal
                    open={instructorViewModalOpen.open}
                    onClose={() => setInstructorViewModalOpen({ open: false, language: 'english' })}
                    tbtTopic={tbtTopic}
                    language={instructorViewModalOpen.language}
                />
            )}

            {/* Submit Confirmation Dialog */}
            <Dialog open={submitDialogOpen} onClose={() => setSubmitDialogOpen(false)}>
                <DialogTitle>Submit Tool Box Talk?</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        Are you sure you want to submit this Tool Box Talk?
                    </Typography>
                    {signedCount < totalCount && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            Only {signedCount} out of {totalCount} employees have signed.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSubmitDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={submitting}
                    >
                        {submitting ? 'Submitting...' : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Loading Backdrop */}
            <Backdrop
                sx={{
                    color: '#fff',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                }}
                open={submitting}
            >
                <CircularProgress color="inherit" size={60} />
                <Typography variant="h6" color="inherit">
                    Submitting Tool Box Talk...
                </Typography>
                <Typography variant="body2" color="inherit">
                    Please wait, generating PDF and uploading...
                </Typography>
            </Backdrop>
        </Box>
    );
};

export default TBTForm;
